import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ModuleProvider } from './contexts/ModuleContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NewExpense from './pages/NewExpense';
import QuickAddExpense from './pages/QuickAddExpense';
import MyRequests from './pages/MyRequests';
import PendingApproval from './pages/PendingApproval';
import PendingPayment from './pages/PendingPayment';
import AllExpenses from './pages/AllExpenses';
import UserManagement from './pages/UserManagement';
import TripsPage from './pages/TripsPage';
import TripDetail from './pages/TripDetail';
import ExportPage from './pages/ExportPage';
import LedgerPage from './pages/LedgerPage';
import SettingsPage from './pages/SettingsPage';
import PermissionsPage from './pages/PermissionsPage';
import { PermissionsProvider } from './contexts/PermissionsContext';
import Sidebar from './components/Sidebar';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const DashboardLayout = ({ children }) => (
  <div className="app-container">
    <Sidebar />
    <div className="main-content">{children}</div>
  </div>
);

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><Dashboard /></DashboardLayout></ProtectedRoute>} />
      <Route path="/trips" element={<ProtectedRoute><DashboardLayout><TripsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/trips/:tripId" element={<ProtectedRoute><DashboardLayout><TripDetail /></DashboardLayout></ProtectedRoute>} />
      <Route path="/expenses/new" element={<ProtectedRoute><DashboardLayout><NewExpense /></DashboardLayout></ProtectedRoute>} />
      <Route path="/expenses/quick-add" element={<ProtectedRoute><DashboardLayout><QuickAddExpense /></DashboardLayout></ProtectedRoute>} />
      <Route path="/expenses/my-requests" element={<ProtectedRoute><DashboardLayout><MyRequests /></DashboardLayout></ProtectedRoute>} />
      <Route path="/expenses/pending-approval" element={<ProtectedRoute><DashboardLayout><PendingApproval /></DashboardLayout></ProtectedRoute>} />
      <Route path="/expenses/pending-payment" element={<ProtectedRoute><DashboardLayout><PendingPayment /></DashboardLayout></ProtectedRoute>} />
      <Route path="/expenses/all" element={<ProtectedRoute><DashboardLayout><AllExpenses /></DashboardLayout></ProtectedRoute>} />
      <Route path="/export" element={<ProtectedRoute><DashboardLayout><ExportPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/ledger" element={<ProtectedRoute><DashboardLayout><LedgerPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/users" element={<AdminRoute><DashboardLayout><UserManagement /></DashboardLayout></AdminRoute>} />
      <Route path="/settings" element={<AdminRoute><DashboardLayout><SettingsPage /></DashboardLayout></AdminRoute>} />
      <Route path="/permissions" element={<AdminRoute><DashboardLayout><PermissionsPage /></DashboardLayout></AdminRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ModuleProvider>
        <BrowserRouter>
          <PermissionsProvider>
            <AppRoutes />
          </PermissionsProvider>
        </BrowserRouter>
      </ModuleProvider>
    </AuthProvider>
  );
}

export default App;
